import React, { useState, useRef, useEffect } from 'react';

/**
 * OTP STORAGE - PART 2
 *
 * Store codes with expiry in database
 * See EVERYTHING about persistence layer
 */

interface StorageEvent {
  id: string;
  timestamp: number;
  layer: 'input' | 'validation' | 'database' | 'network' | 'storage' | 'index';
  type: 'info' | 'success' | 'error' | 'process' | 'detail';
  message: string;
  details?: any;
  duration?: number;
}

interface StoredOTP {
  id: string;
  email: string;
  code: string;
  purpose: string;
  expiresAt: Date;
  used: boolean;
  createdAt: Date;
}

export default function OTPStorage() {
  const [events, setEvents] = useState<StorageEvent[]>([]);
  const [isStoring, setIsStoring] = useState(false);
  const [email, setEmail] = useState('test@example.com');
  const [currentCode, setCurrentCode] = useState('');
  const [storedCodes, setStoredCodes] = useState<StoredOTP[]>([]);
  const [showDatabase, setShowDatabase] = useState(false);
  const eventsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const logEvent = (event: Omit<StorageEvent, 'id' | 'timestamp'>) => {
    const newEvent: StorageEvent = {
      ...event,
      id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };

    setEvents(prev => [...prev, newEvent]);
    return new Promise(resolve => setTimeout(resolve, event.duration || 50));
  };

  const generateCode = () => {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return ((array[0] % 900000) + 100000).toString();
  };

  const storeOTP = async () => {
    setIsStoring(true);
    setEvents([]);
    const code = generateCode();
    setCurrentCode(code);
    const startTime = Date.now();

    try {
      // Step 1: Input validation
      await logEvent({
        layer: 'input',
        type: 'info',
        message: 'Validating input parameters',
        details: {
          email,
          code,
          purpose: 'login',
          codeLength: code.length,
          emailValid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        }
      });

      // Step 2: Generate storage ID
      const id = `otp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const expiresAt = new Date(Date.now() + 10 * 60 * 1000); // 10 minutes

      await logEvent({
        layer: 'storage',
        type: 'process',
        message: 'Preparing storage record',
        details: {
          id,
          expiresAt: expiresAt.toISOString(),
          expiresIn: '10 minutes',
          ttl: 600,
          timestamp: Date.now()
        },
        duration: 75
      });

      // Step 3: Simulate SQL preparation
      const sql = `INSERT INTO otp_codes (id, email, code, purpose, expires_at, created_at) VALUES (?, ?, ?, ?, ?, ?)`;

      await logEvent({
        layer: 'database',
        type: 'process',
        message: 'Preparing SQL statement',
        details: {
          sql,
          bindings: [id, email, code, 'login', expiresAt.toISOString(), new Date().toISOString()],
          parameterCount: 6
        }
      });

      // Step 4: Network request to CloudFlare Worker
      await logEvent({
        layer: 'network',
        type: 'process',
        message: 'Sending to CloudFlare D1',
        details: {
          endpoint: '/api/otp/store',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          payload: {
            email,
            code,
            purpose: 'login'
          }
        },
        duration: 100
      });

      // Step 5: Database write
      await logEvent({
        layer: 'database',
        type: 'process',
        message: 'Writing to D1 database',
        details: {
          table: 'otp_codes',
          operation: 'INSERT',
          durableObject: false,
          consistency: 'eventual',
          location: 'CloudFlare Edge'
        },
        duration: 150
      });

      // Step 6: Index updates
      await logEvent({
        layer: 'index',
        type: 'process',
        message: 'Updating database indexes',
        details: {
          indexes: [
            'idx_otp_email_code',
            'idx_otp_expires',
            'idx_otp_created'
          ],
          cost: '3 index writes'
        }
      });

      // Step 7: Success confirmation
      const newOTP: StoredOTP = {
        id,
        email,
        code,
        purpose: 'login',
        expiresAt,
        used: false,
        createdAt: new Date()
      };

      setStoredCodes(prev => [...prev, newOTP]);

      await logEvent({
        layer: 'storage',
        type: 'success',
        message: `✅ OTP Stored Successfully`,
        details: {
          id,
          code,
          email,
          expiresAt: expiresAt.toISOString(),
          storageTime: `${Date.now() - startTime}ms`
        }
      });

      // Step 8: Storage verification
      await logEvent({
        layer: 'validation',
        type: 'info',
        message: 'Storage verification',
        details: {
          persisted: true,
          queryable: true,
          indexed: true,
          ttlSet: true,
          willExpireAt: expiresAt.toLocaleString()
        }
      });

    } catch (error) {
      await logEvent({
        layer: 'storage',
        type: 'error',
        message: 'Storage failed',
        details: { error: error instanceof Error ? error.message : 'Unknown error' }
      });
    } finally {
      setIsStoring(false);
    }
  };

  const simulateBulkStorage = async () => {
    setEvents([]);
    const startTime = Date.now();

    await logEvent({
      layer: 'storage',
      type: 'info',
      message: 'Starting bulk storage test',
      details: { count: 5 }
    });

    for (let i = 0; i < 5; i++) {
      const code = generateCode();
      const id = `otp-bulk-${i}-${Date.now()}`;
      const expiresAt = new Date(Date.now() + (10 - i) * 60 * 1000); // Varying expiry

      await logEvent({
        layer: 'database',
        type: 'detail',
        message: `Storing code ${i + 1}`,
        details: {
          id,
          code,
          email: `user${i + 1}@test.com`,
          expiresIn: `${10 - i} minutes`
        },
        duration: 30
      });

      setStoredCodes(prev => [...prev, {
        id,
        email: `user${i + 1}@test.com`,
        code,
        purpose: 'login',
        expiresAt,
        used: false,
        createdAt: new Date()
      }]);
    }

    await logEvent({
      layer: 'storage',
      type: 'success',
      message: 'Bulk storage complete',
      details: {
        totalStored: 5,
        totalTime: `${Date.now() - startTime}ms`,
        averageTime: `${(Date.now() - startTime) / 5}ms per code`
      }
    });
  };

  const cleanupExpired = async () => {
    setEvents([]);
    const now = Date.now();
    const expired = storedCodes.filter(otp => otp.expiresAt.getTime() < now);

    await logEvent({
      layer: 'storage',
      type: 'info',
      message: 'Running cleanup sweep',
      details: {
        totalCodes: storedCodes.length,
        expiredCount: expired.length,
        currentTime: new Date().toISOString()
      }
    });

    if (expired.length > 0) {
      await logEvent({
        layer: 'database',
        type: 'process',
        message: 'Deleting expired codes',
        details: {
          sql: `DELETE FROM otp_codes WHERE expires_at < datetime('now')`,
          affectedRows: expired.length
        },
        duration: 100
      });

      setStoredCodes(prev => prev.filter(otp => otp.expiresAt.getTime() >= now));

      await logEvent({
        layer: 'storage',
        type: 'success',
        message: `Cleaned ${expired.length} expired codes`,
        details: {
          deletedIds: expired.map(e => e.id),
          freedSpace: `~${expired.length * 256} bytes`
        }
      });
    } else {
      await logEvent({
        layer: 'storage',
        type: 'info',
        message: 'No expired codes to clean',
        details: { nextCleanup: 'in 5 minutes' }
      });
    }
  };

  const getLayerColor = (layer: string) => {
    const colors: Record<string, string> = {
      input: 'text-blue-400',
      validation: 'text-purple-400',
      database: 'text-yellow-400',
      network: 'text-green-400',
      storage: 'text-cyan-400',
      index: 'text-pink-400'
    };
    return colors[layer] || 'text-gray-400';
  };

  const getLayerIcon = (layer: string) => {
    const icons: Record<string, string> = {
      input: '📝',
      validation: '✓',
      database: '🗄️',
      network: '🌐',
      storage: '💾',
      index: '🔍'
    };
    return icons[layer] || '•';
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">💾 OTP STORAGE - PART 2</h1>
          <p className="text-gray-400">Store codes with expiry timestamp</p>
          <p className="text-yellow-400 text-sm mt-2">Watch data persist to CloudFlare D1</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Left: Controls and Storage */}
          <div className="space-y-4">
            {/* Input */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-green-400">📧 INPUT</h2>

              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-sm">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-600 p-2 text-green-400 mt-1"
                    placeholder="user@example.com"
                  />
                </div>

                {currentCode && (
                  <div className="bg-gray-900 border border-green-400 p-4 text-center">
                    <div className="text-gray-400 text-sm">Last Generated Code</div>
                    <div className="text-3xl text-green-400 font-bold mt-2">{currentCode}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">🎮 CONTROLS</h2>

              <div className="space-y-2">
                <button
                  onClick={storeOTP}
                  disabled={isStoring}
                  className="w-full bg-green-400 text-black p-3 hover:bg-green-300 disabled:opacity-50 font-bold"
                >
                  {isStoring ? '💾 STORING...' : '💾 GENERATE & STORE OTP'}
                </button>

                <button
                  onClick={simulateBulkStorage}
                  disabled={isStoring}
                  className="w-full bg-blue-400 text-black p-3 hover:bg-blue-300 disabled:opacity-50"
                >
                  📦 BULK STORE (5 codes)
                </button>

                <button
                  onClick={cleanupExpired}
                  className="w-full bg-yellow-400 text-black p-3 hover:bg-yellow-300"
                >
                  🧹 CLEANUP EXPIRED
                </button>

                <button
                  onClick={() => setShowDatabase(!showDatabase)}
                  className="w-full bg-purple-400 text-black p-3 hover:bg-purple-300"
                >
                  {showDatabase ? '📊 HIDE DATABASE' : '📊 SHOW DATABASE'}
                </button>

                <button
                  onClick={() => {
                    setEvents([]);
                    setStoredCodes([]);
                    setCurrentCode('');
                  }}
                  className="w-full bg-gray-700 text-white p-3 hover:bg-gray-600"
                >
                  🔄 CLEAR ALL
                </button>
              </div>
            </div>

            {/* Database View */}
            {showDatabase && (
              <div className="border border-purple-400 p-4">
                <h2 className="text-xl mb-4 text-purple-400">🗄️ DATABASE STATE</h2>

                <div className="text-xs space-y-2 max-h-64 overflow-y-auto">
                  {storedCodes.length === 0 ? (
                    <div className="text-gray-600 text-center py-4">No codes stored yet</div>
                  ) : (
                    storedCodes.map((otp, i) => {
                      const isExpired = otp.expiresAt.getTime() < Date.now();
                      return (
                        <div
                          key={otp.id}
                          className={`border p-2 ${
                            isExpired ? 'border-red-600 opacity-50' :
                            otp.used ? 'border-gray-600' :
                            'border-green-600'
                          }`}
                        >
                          <div className="flex justify-between">
                            <span className="text-gray-400">#{i + 1}</span>
                            <span className={isExpired ? 'text-red-400' : 'text-green-400'}>
                              {otp.code}
                            </span>
                          </div>
                          <div className="text-gray-500 mt-1">
                            <div>{otp.email}</div>
                            <div>Expires: {otp.expiresAt.toLocaleTimeString()}</div>
                            {isExpired && <div className="text-red-400">EXPIRED</div>}
                            {otp.used && <div className="text-yellow-400">USED</div>}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="mt-4 text-sm text-gray-400">
                  <div>Total: {storedCodes.length} codes</div>
                  <div>Active: {storedCodes.filter(o => !o.used && o.expiresAt.getTime() > Date.now()).length}</div>
                  <div>Expired: {storedCodes.filter(o => o.expiresAt.getTime() < Date.now()).length}</div>
                  <div>Used: {storedCodes.filter(o => o.used).length}</div>
                </div>
              </div>
            )}

            {/* Storage Info */}
            <div className="border border-cyan-400 p-4">
              <h2 className="text-xl mb-4 text-cyan-400">💡 STORAGE INFO</h2>
              <div className="text-xs text-gray-400 space-y-1">
                <div>• CloudFlare D1 SQLite database</div>
                <div>• 10-minute expiry window</div>
                <div>• Indexed on email + code</div>
                <div>• Automatic TTL enforcement</div>
                <div>• One-time use enforcement</div>
              </div>
            </div>
          </div>

          {/* Right: Event Stream */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">📡 STORAGE EVENT STREAM</h2>

            <div className="h-[700px] overflow-y-auto space-y-2 text-xs font-mono">
              {events.length === 0 ? (
                <div className="text-gray-600 text-center py-8">
                  Waiting for storage events...
                </div>
              ) : (
                events.map(event => (
                  <div
                    key={event.id}
                    className={`border-l-2 pl-2 ${
                      event.type === 'success' ? 'border-green-400' :
                      event.type === 'error' ? 'border-red-400' :
                      event.type === 'process' ? 'border-blue-400' :
                      event.type === 'detail' ? 'border-purple-400' :
                      'border-gray-400'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{getLayerIcon(event.layer)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">
                            [{new Date(event.timestamp).toLocaleTimeString('en-US', {
                              hour12: false,
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              fractionalSecondDigits: 3
                            })}]
                          </span>
                          <span className={`font-bold ${getLayerColor(event.layer)}`}>
                            {event.layer.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-gray-300 mt-1">{event.message}</div>
                        {event.details && (
                          <pre className="text-gray-500 mt-2 bg-gray-900 p-2 rounded overflow-x-auto">
{JSON.stringify(event.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={eventsEndRef} />
            </div>
          </div>
        </div>

        {/* Bottom: Complete Picture */}
        {storedCodes.length > 0 && (
          <div className="mt-6 border border-cyan-400 p-4">
            <h2 className="text-xl text-cyan-400 mb-2">🎯 STORAGE COMPLETE!</h2>
            <p className="text-gray-400 mb-4">
              You've stored {storedCodes.length} OTP codes in the database with proper expiry timestamps.
              Each code is indexed for fast retrieval and will automatically expire after 10 minutes.
            </p>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-400 font-bold">Input Layer:</span>
                <div className="text-gray-400">
                  • Email validation<br />
                  • Code generation<br />
                  • Purpose tagging
                </div>
              </div>
              <div>
                <span className="text-yellow-400 font-bold">Database Layer:</span>
                <div className="text-gray-400">
                  • SQL preparation<br />
                  • D1 SQLite storage<br />
                  • Index updates
                </div>
              </div>
              <div>
                <span className="text-green-400 font-bold">Network Layer:</span>
                <div className="text-gray-400">
                  • CloudFlare Worker<br />
                  • Edge location<br />
                  • Global replication
                </div>
              </div>
              <div>
                <span className="text-cyan-400 font-bold">Storage Properties:</span>
                <div className="text-gray-400">
                  • 10-min expiry<br />
                  • One-time use<br />
                  • Auto-cleanup ready
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}